import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

/**
 * Proposal card for `decide_ticket_approval` — the manager approves or rejects
 * a ticket awaiting their approval directly in the chat. The decision
 * (approve / reject) is fixed by the LLM; the note is editable here and is
 * required when rejecting.
 */
export default function TicketApprovalProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
}) {
  const summary = proposal.summary || {};
  const isApprove = summary.decision === "approve";
  const ticketNumber = summary.ticket_number;

  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState(summary.note || "");

  // Sync when the proposal is externally cancelled (e.g. a sibling bulk action).
  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  // A rejection must carry a reason; approval notes are optional.
  const confirmDisabled = loading || (!isApprove && !note.trim());

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };
      if (action === "confirm") {
        // Approvals carry no note (consistent with the ticket drawer);
        // only rejections require a reason.
        payload.overrides = { note: isApprove ? "" : note.trim() };
      }
      const res = await postRightworkAPI(API_ops_assistant_confirm_action, payload);
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process the decision.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          err?.response?.data?.error ||
          "Failed to process the decision."
      );
    }
    setLoading(false);
  };

  // Confirmed — the decision was carried out.
  if (status === "confirmed") {
    if (isApprove) {
      return (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
          <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="font-medium">Approved ticket #{ticketNumber}</div>
            {summary.ticket_title && (
              <div className="text-xs opacity-80 truncate">{summary.ticket_title}</div>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 my-2">
        <CancelIcon fontSize="small" className="text-red-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">Rejected ticket #{ticketNumber}</div>
          {summary.ticket_title && (
            <div className="text-xs opacity-80 truncate">{summary.ticket_title}</div>
          )}
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Action cancelled</span>
      </div>
    );
  }

  // Pending — full card, tinted by the decision.
  const accent = isApprove
    ? {
        border: "border-emerald-200",
        bg: "bg-emerald-50",
        headBg: "bg-emerald-100",
        headBorder: "border-emerald-200",
        headText: "text-emerald-800",
        icon: "text-emerald-700",
        btn: "bg-emerald-600 hover:bg-emerald-700",
      }
    : {
        border: "border-red-200",
        bg: "bg-red-50",
        headBg: "bg-red-100",
        headBorder: "border-red-200",
        headText: "text-red-800",
        icon: "text-red-700",
        btn: "bg-red-600 hover:bg-red-700",
      };

  return (
    <div className={`my-2 max-w-md rounded-lg border ${accent.border} ${accent.bg}`}>
      <div
        className={`flex items-center gap-2 px-3 py-2 ${accent.headBg} border-b ${accent.headBorder} rounded-t-lg`}
      >
        {isApprove ? (
          <ThumbUpOffAltIcon fontSize="small" className={accent.icon} />
        ) : (
          <ThumbDownOffAltIcon fontSize="small" className={accent.icon} />
        )}
        <span className={`text-sm font-medium ${accent.headText}`}>
          {isApprove ? "Approve" : "Reject"} ticket #{ticketNumber}
        </span>
      </div>

      <div className="px-3 py-2 space-y-2">
        {summary.ticket_title && (
          <div className="text-sm font-medium text-gray-700">{summary.ticket_title}</div>
        )}
        {/* Note only on reject — approvals are a one-tap confirm, matching
            the ticket drawer. */}
        {!isApprove && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Reason for rejection
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={2000}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
              placeholder="Explain what needs to change..."
            />
            {!note.trim() && (
              <div className="text-[11px] text-red-500 mt-0.5">
                A reason is required to reject.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 px-3 py-2 border-t border-gray-200 bg-white rounded-b-lg">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={confirmDisabled}
          className={`px-3 py-1.5 text-sm rounded-md text-white disabled:opacity-50 flex items-center gap-1 ${accent.btn}`}
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          {isApprove ? "Approve" : "Reject"}
        </button>
      </div>
    </div>
  );
}
